import React from 'react';
import { CreateShippingRecord, Driver, Vehicle } from '../../types';
import { formatVietnamCurrency } from '../../utils/numberFormatter';
import { FieldRow } from '../shipping/FieldRow';

interface ShippingRecordFormFieldsProps {
  t: (key: string) => string;
  formData: CreateShippingRecord;
  setFormData: React.Dispatch<React.SetStateAction<CreateShippingRecord>>;
  provinces: any[];
  districts: any[];
  vehicles: Vehicle[];
  drivers: Driver[];
  updateLoadUnloadFee: () => void;
  updateShippingPrice: () => void;
  updateTollFee: () => void;
  updateRM2Fee: () => void;
  updateSalaryRate: () => void;
  formatNumberWithDot: (value: number) => string;
  onCalculateAllCosts?: () => void;
}

const ShippingRecordFormFields: React.FC<ShippingRecordFormFieldsProps> = ({
  t,
  formData,
  setFormData,
  provinces,
  districts,
  vehicles,
  drivers,
  updateLoadUnloadFee,
  updateShippingPrice,
  updateTollFee,
  updateRM2Fee,
  updateSalaryRate,
  formatNumberWithDot,
  onCalculateAllCosts
}) => {
  const canCalculateCosts = formData.licensePlate && formData.shippingDate && formData.provinceId;

  return (
    <>
      <FieldRow label={t('customer.name')} required>
     <input 
  type="text" 
          className="form-control" 
    value={formData.customerName}
       onChange={e => setFormData({ ...formData, customerName: e.target.value })} 
          required 
        />
      </FieldRow>

      <FieldRow label={t('customer.phone')}>
     <input 
type="tel" 
 className="form-control" 
          value={formData.customerPhone}
 onChange={e => setFormData({ ...formData, customerPhone: e.target.value })} 
        />
      </FieldRow>

      <FieldRow label={t('customer.email')}>
        <input 
   type="email" 
          className="form-control" 
          value={formData.customerEmail}
       onChange={e => setFormData({ ...formData, customerEmail: e.target.value })} 
    />
      </FieldRow>

      <FieldRow label={t('shipping.address')} required>
        <textarea 
     className="form-control" 
          value={formData.shippingAddress}
          onChange={e => setFormData({ ...formData, shippingAddress: e.target.value })} 
 required 
        />
    </FieldRow>

      <FieldRow label={t('province')} required>
        <select
        className="form-control"
  value={formData.provinceId || ''}
          onChange={e => {
            const provinceId = parseInt(e.target.value) || undefined;
   setFormData(prev => ({
          ...prev,
      provinceId,
              districtId: undefined
}));
  }}
    required
        >
       <option value="">{t('please.select')}</option>
          {provinces.map((province: any) => (
      <option key={province.id} value={province.id}>
        {province.name}
      </option>
        ))}
        </select>
      </FieldRow>

      <FieldRow label={t('district')} required>
 <select
          className="form-control"
          value={formData.districtId || ''}
          onChange={e => {
            const districtId = parseInt(e.target.value) || undefined;
    setFormData(prev => ({
     ...prev,
              districtId
    }));
     }}
          required
     disabled={!formData.provinceId}
        >
   <option value="">{t('please.select')}</option>
        {districts.map((district: any) => (
        <option key={district.id} value={district.id}>
          {district.name}
         </option>
          ))}
        </select>
      </FieldRow>

    <FieldRow label={t('vehicle')} required>
     <select 
          className="form-control" 
      value={formData.licensePlate}
       onChange={e => {
        const selectedVehicle = vehicles.find(v => v.licensePlate === e.target.value);
     let preferredDriver: Driver | undefined = undefined;
     if (selectedVehicle) {
              preferredDriver = drivers.find(d => Array.isArray(d.preferredVehicleIds) && d.preferredVehicleIds.includes(selectedVehicle.id));
   }
            setFormData(prev => ({
   ...prev,
  licensePlate: e.target.value,
              vehicleId: selectedVehicle?.id,
        driverId: preferredDriver ? preferredDriver.id : prev.driverId,
    driverName: preferredDriver ? preferredDriver.name : prev.driverName,
       driverPhone: preferredDriver ? preferredDriver.phone : prev.driverPhone
         }));
        }} 
          required
      >
      <option value="">{t('please.select')}</option>
        {vehicles.map(vehicle => (
         <option key={vehicle.id} value={vehicle.licensePlate}>
      {vehicle.licensePlate} - {vehicle.brand} {vehicle.model}
              {vehicle.capacityTons && ` (${vehicle.capacityTons}T)`}
            </option>
        ))}
        </select>
        <small className="form-text text-muted">
  {t('fees.auto.calculated.based.on.vehicle.capacity.and.destination')}
        </small>
      </FieldRow>

      <FieldRow label={t('shipping.date')} required>
        <input 
       type="date" 
 className="form-control" 
    value={formData.shippingDate}
          onChange={e => setFormData({ ...formData, shippingDate: e.target.value })} 
          required 
        />
      </FieldRow>

      {/* Calculate Costs Button - Professional Design with Proper Alignment */}
      <div className="row mb-3">
        <div className="col-md-3"></div>
        <div className="col-md-9">
          <div className="card border-primary" style={{ 
            borderWidth: '2px',
      backgroundColor: canCalculateCosts ? '#f0f8ff' : '#f8f9fa',
            transition: 'all 0.3s ease'
          }}>
    <div className="card-body" style={{ padding: '1rem' }}>
   <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="flex-grow-1">
         <h6 className="mb-1 fw-bold text-primary" style={{ fontSize: '0.95rem' }}>
           <i className="bi bi-calculator-fill me-2"></i>
       {t('auto.calculate.all.costs') || 'Automatic Cost Calculation'}
          </h6>
      <small className="text-muted d-block" style={{ fontSize: '0.85rem' }}>
           {canCalculateCosts 
  ? (t('ready.to.calculate') || 'Ready to calculate all fees automatically')
       : (t('select.vehicle.date.province.to.calculate') || 'Select vehicle, shipping date, and province to calculate')
         }
   </small>
     </div>
           <div>
    <button
     type="button"
     className="btn btn-primary d-flex align-items-center gap-2"
              onClick={onCalculateAllCosts}
        disabled={!canCalculateCosts}
     style={{
           minWidth: '160px',
      fontWeight: '600',
        fontSize: '0.9rem',
         padding: '0.5rem 1rem',
        boxShadow: canCalculateCosts ? '0 4px 6px rgba(13, 110, 253, 0.2)' : 'none',
           transform: canCalculateCosts ? 'scale(1)' : 'scale(0.98)',
       transition: 'all 0.2s ease'
        }}
          onMouseEnter={(e) => {
       if (canCalculateCosts) {
   e.currentTarget.style.transform = 'scale(1.02)';
        e.currentTarget.style.boxShadow = '0 6px 12px rgba(13, 110, 253, 0.3)';
          }
}}
                 onMouseLeave={(e) => {
     e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = canCalculateCosts ? '0 4px 6px rgba(13, 110, 253, 0.2)' : 'none';
     }}
                  >
              <i className="bi bi-lightning-charge-fill"></i>
          <span>{t('calculate.costs') || 'Calculate Costs'}</span>
         </button>
      </div>
              </div>
      
        {/* Information badges */}
      {canCalculateCosts && (
    <div className="mt-2 d-flex flex-wrap gap-2">
     <span className="badge bg-success bg-opacity-10 text-success border border-success" style={{ fontSize: '0.75rem' }}>
            <i className="bi bi-check-circle-fill me-1"></i>
         {t('will.calculate.driver.salary') || 'Driver Salary'}
        </span>
    <span className="badge bg-success bg-opacity-10 text-success border border-success" style={{ fontSize: '0.75rem' }}>
             <i className="bi bi-check-circle-fill me-1"></i>
       {t('will.calculate.loading.fee') || 'Loading Fee'}
  </span>
 <span className="badge bg-success bg-opacity-10 text-success border border-success" style={{ fontSize: '0.75rem' }}>
       <i className="bi bi-check-circle-fill me-1"></i>
   {t('will.calculate.shipping.cost') || 'Shipping Cost'}
      </span>
    <span className="badge bg-success bg-opacity-10 text-success border border-success" style={{ fontSize: '0.75rem' }}>
     <i className="bi bi-check-circle-fill me-1"></i>
          {t('will.calculate.toll.fee') || 'Toll Fee'}
      </span>
 <span className="badge bg-success bg-opacity-10 text-success border border-success" style={{ fontSize: '0.75rem' }}>
              <i className="bi bi-check-circle-fill me-1"></i>
   {t('will.calculate.rm2') || 'RM2 Fee'}
     </span>
             </div>
      )}
          </div>
   </div>
        </div>
      </div>

      <FieldRow label={t('driver.name')} required>
        <select
        className="form-control"
 value={formData.driverId || ''}
          onChange={e => {
  const driverId = parseInt(e.target.value) || undefined;
          const selectedDriver = drivers.find(d => d.id === driverId);
       setFormData(prev => ({
  ...prev,
        driverId,
           driverName: selectedDriver ? selectedDriver.name : '',
        driverPhone: selectedDriver ? selectedDriver.phone : ''
            }));
          }}
          required
    >
          <option value="">{t('please.select')}</option>
          {drivers.map(driver => (
    <option key={driver.id} value={driver.id}>
            {driver.name} {driver.phone ? `(${driver.phone})` : ''}
          </option>
          ))}
        </select>
      </FieldRow>

      <FieldRow label={t('salary.rate')} required>
   <div className="input-group">
          <input 
    type="number" 
            step="0.01" 
min="0" 
          max="100" 
        className="form-control" 
         value={formData.salaryRate ? (formData.salaryRate * 100) : ''}
          onChange={e => setFormData({ ...formData, salaryRate: parseFloat(e.target.value) / 100 })}
            required 
        />
          <span className="input-group-text">%</span>
 <button 
            type="button" 
            className="btn btn-outline-secondary" 
            onClick={updateSalaryRate}
            title="Update salary rate from database"
    disabled={!formData.driverId || !formData.shippingDate}
        >
    🔄
          </button>
        </div>
     {(!formData.driverId || !formData.shippingDate) && (
 <small className="form-text text-muted">
            {t('select.driver.and.date.first') || 'Select driver and shipping date first'}
          </small>
        )}
      </FieldRow>

      <FieldRow label={t('driver.salary')}>
        <input 
        type="text" 
          className="form-control" 
          value={formatVietnamCurrency((formData.shippingCost || 0) * (formData.salaryRate || 0))} 
      readOnly 
        />
      </FieldRow>

      <FieldRow label={t('loading.fee')}>
        <div className="input-group">
          <input 
            type="text" 
            className="form-control"
      value={formatNumberWithDot(formData.loadUnloadFee)}
      onChange={e => {
      const raw = e.target.value.replace(/\./g, '');
setFormData({ ...formData, loadUnloadFee: parseFloat(raw) || 0 });
            }} 
        />
<button 
         type="button" 
            className="btn btn-outline-secondary" 
    onClick={updateLoadUnloadFee}
    title="Update fee from database"
          >
          🔄
          </button>
        </div>
      </FieldRow>

      <FieldRow label={t('shipping.cost')}>
        <div className="input-group">
          <input 
            type="text" 
    className="form-control"
       value={formatNumberWithDot(formData.shippingCost)}
            onChange={e => {
const raw = e.target.value.replace(/\./g, '');
      setFormData({ ...formData, shippingCost: parseFloat(raw) || 0 });
            }} 
          />
          <button 
            type="button" 
 className="btn btn-outline-secondary" 
        onClick={updateShippingPrice}
            title="Update cost from database"
          >
    🔄
     </button>
        </div>
      </FieldRow>

   <FieldRow label={t('toll.fee')}>
        <div className="input-group">
      <input 
          type="text" 
className="form-control"
 value={formatNumberWithDot(formData.tollFee)}
        onChange={e => {
          const raw = e.target.value.replace(/\./g, '');
    setFormData({ ...formData, tollFee: parseFloat(raw) || 0 });
            }} 
      />
    <button 
      type="button" 
            className="btn btn-outline-secondary" 
          onClick={updateTollFee}
title="Update fee from database"
        >
   🔄
          </button>
        </div>
      </FieldRow>

      <FieldRow label={t('rm2')}>
        <div className="input-group">
          <input 
     type="text" 
   className="form-control"
 value={formatNumberWithDot(formData.rm2Fee)}
      onChange={e => {
    const raw = e.target.value.replace(/\./g, '');
              setFormData({ ...formData, rm2Fee: parseFloat(raw) || 0 });
            }} 
          />
<button 
            type="button" 
            className="btn btn-outline-secondary"
 onClick={updateRM2Fee}
 title="Update RM2 fee from database"
     >
            🔄
          </button>
      </div>
      </FieldRow>

      <FieldRow label={t('extra.toll.fee.1')}>
        <input 
          type="text" 
          className="form-control" 
     value={formatNumberWithDot(formData.extraTollFee1)}
          onChange={e => {
            const raw = e.target.value.replace(/\./g, '');
            setFormData({ ...formData, extraTollFee1: parseFloat(raw) || 0 });
    }} 
        />
      </FieldRow>

   <FieldRow label={t('extra.toll.fee.2')}>
        <input 
          type="text" 
          className="form-control" 
          value={formatNumberWithDot(formData.extraTollFee2)}
 onChange={e => {
     const raw = e.target.value.replace(/\./g, '');
            setFormData({ ...formData, extraTollFee2: parseFloat(raw) || 0 });
          }} 
   />
      </FieldRow>

      <FieldRow label={t('prepaid.amount')}>
     <input 
          type="text" 
        className="form-control" 
    value={formatNumberWithDot(formData.prepaidAmount)}
    onChange={e => {
  const raw = e.target.value.replace(/\./g, '');
            setFormData({ ...formData, prepaidAmount: parseFloat(raw) || 0 });
  }} 
        readOnly 
        />
      </FieldRow>

      <FieldRow label={t('notes')}>
        <textarea 
          className="form-control"
     value={formData.notes}
          onChange={e => setFormData({ ...formData, notes: e.target.value })} 
        />
</FieldRow>
    </>
  );
};

export default ShippingRecordFormFields;
